import React, { useMemo } from 'react';
import { AI_ENDPOINTS } from 'features/ai-generators/constants';
import useDatatable, { getColumnPreferences, getColumnsToRender } from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { SECONDARY_API_CLIENT } from 'services/API';
import { SEARCH_PROJECT_CREATED_FROM_VARIANTS } from 'services/searchProjects';
import {
  ScrollableDataTable,
  ScrollableDataTableLayout
} from 'UI/components/templates/ScrollableDataTableLayout';
import { EntityType } from 'UI/constants/entityTypes';

import {
  COPIES,
  DEFAULT_PER_PAGE,
  ORDER_BY_OPTIONS,
  PROSPECTING_TABLE_CONFIG_BY_TYPE
} from './AIProspectingResultsTable.constants';

export const AIProspectingResultsTable = ({
  entityType = EntityType.Candidate,
  responseId = null,
  shouldScrollOnNavigate = false
}) => {
  const {
    columns: columnsDefinitions,
    extraPayloadData,
    tableKey
  } = PROSPECTING_TABLE_CONFIG_BY_TYPE[entityType];

  const initialPreferences = getColumnPreferences(
    tableKey,
    0,
    ORDER_BY_OPTIONS,
    columnsDefinitions
  );

  const {
    columnPreferences,
    count,
    data,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState,
    queryParams
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: initialPreferences,
    endpoint: responseId
      ? `${AI_ENDPOINTS.prospectsSourcingByResponse.replace(':responseId', responseId)}`
      : null,
    initialPreferences,
    initialUiState: {
      perPage: DEFAULT_PER_PAGE
    },
    key: tableKey,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    sendDateWithTimezone: true,
    shouldExcludeUserFilterParam: true,
    shouldScrollOnNavigate,
    shouldUseSavedPagination: false
  });

  const { isLoading, showWholeSkeleton, page, perPage } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const dataForSelection = useMemo(() => data.map(({ id }) => ({ id, type: 'Item' })), [data]);

  const {
    filteredItems,
    handleRowSelection,
    multiSelectComponents,
    selectedIndexesInPage,
    totalSelected
  } = useMultipleSelection({ data: dataForSelection, count, disableSelectAllCheckbox: true });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    createdFrom: SEARCH_PROJECT_CREATED_FROM_VARIANTS.prospectingAssistant,
    filteredItems,
    queryParams,
    shouldShowAddToSearchProjectAction: false,
    extraPayloadData
  });

  const finalColumns = getColumnsToRender(columns);

  return (
    <>
      <ScrollableDataTableLayout
        content={
          <ScrollableDataTable
            columnOrder={columnOrder?.length ? columnOrder : undefined}
            columns={finalColumns}
            components={multiSelectComponents}
            count={count ?? 0}
            CustomToolbar={
              <CustomTableToolbarSelect total={totalSelected}>
                <SearchProjectAction />
              </CustomTableToolbarSelect>
            }
            data={data}
            elevation={0}
            filter={false}
            loading={showWholeSkeleton}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnSortChange={handleColumnSortChange}
            onPageClick={handlePageChange}
            onPerPageClick={handlePerPageChange}
            onRowSelectionChange={handleRowSelection}
            onSearchTextChange={handleKeywordChange}
            onToggleFilters={handleFiltersToggle}
            page={page}
            refreshing={isLoading}
            rowsPerPage={perPage}
            rowsPerPageOptions={[DEFAULT_PER_PAGE]}
            rowsSelected={selectedIndexesInPage}
            search={false}
            selectToolbarPlacement="none"
            showGoToPageInput={false}
            sortOrder={{ name: orderBy, direction }}
            title={COPIES.emptyState.title}
          />
        }
      />

      <SearchProjectForms />
    </>
  );
};
